import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import type { Car, Conversation, SingleResponse } from '../api/types';

interface MessageOwnerButtonProps {
  car: Car;
}

export function MessageOwnerButton({ car }: MessageOwnerButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleOpen = () => {
    setError('');
    setBody('');
    setOpen(true);
  };

  const handleClose = () => {
    if (sending) return;
    setOpen(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    setError('');
    try {
      const res = await apiFetch<SingleResponse<Conversation>>('/api/v1/conversations', {
        method: 'POST',
        body: JSON.stringify({ car_id: car.id, body: body.trim() }),
      });
      setOpen(false);
      navigate(`/inbox/${res.data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ChatBubbleOutlineIcon />}
        onClick={handleOpen}
      >
        {t('inbox.messageOwner')}
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {t('inbox.startConversationTitle', {
              owner: car.user?.name ?? '—',
              car: car.display_name,
            })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t('inbox.startConversationBody')}
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={4}
              placeholder={t('inbox.sendPlaceholder')}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={sending}
              error={Boolean(error)}
              helperText={error || undefined}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={sending}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={sending || !body.trim()}
            >
              {sending ? t('inbox.sending') : t('inbox.send')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
